/* ==========================================================================
   PROCEDURAL ITEM GENERATOR
   --------------------------------------------------------------------------
   The curated bank in exam-bank.js holds hand-written items. This module
   builds additional quantitative and applied items on demand so that a course
   sitting can reach 400 (free) or up to 2000 (premium) questions without
   repeating a stem verbatim.

   Every generated item carries a worked explanation, not just a key —
   an unexplained item teaches nothing.

   Generation is deterministic: the same seed always yields the same paper.
   That matters because a student who retakes "Midterm, seed 4821" gets the
   identical exam, and an instructor can reference a specific form.
   ========================================================================== */

(function () {
  'use strict';

  /* ---- Deterministic PRNG (mulberry32) -------------------------------- */
  function rng(seed) {
    var t = seed >>> 0;
    return function () {
      t = (t + 0x6D2B79F5) >>> 0;
      var x = t;
      x = Math.imul(x ^ (x >>> 15), 1 | x);
      x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x;
      return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
    };
  }
  function ri(r, lo, hi) { return lo + Math.floor(r() * (hi - lo + 1)); }
  function pick(r, arr) { return arr[Math.floor(r() * arr.length)]; }

  /* Build a 4-option MCQ from a correct value plus plausible wrong values */
  function mcq(r, correct, distractors, fmt) {
    fmt = fmt || function (v) { return String(v); };
    var seen = {}, opts = [];
    seen[String(correct)] = true;
    opts.push(correct);
    for (var i = 0; i < distractors.length && opts.length < 4; i++) {
      var d = distractors[i];
      if (d === null || d === undefined || !isFinite(d) || seen[String(d)]) continue;
      seen[String(d)] = true;
      opts.push(d);
    }
    var guard = 0;
    while (opts.length < 4 && guard++ < 40) {
      var jitter = correct * (1 + (r() - 0.5) * 0.6);
      jitter = Math.round(jitter * 100) / 100;
      if (!seen[String(jitter)] && jitter !== 0) { seen[String(jitter)] = true; opts.push(jitter); }
    }
    /* shuffle */
    for (var j = opts.length - 1; j > 0; j--) {
      var k = Math.floor(r() * (j + 1)); var tmp = opts[j]; opts[j] = opts[k]; opts[k] = tmp;
    }
    return { opts: opts.map(fmt), a: opts.indexOf(correct) };
  }

  var YT = function (q, label) {
    return { t: label || ('YouTube — ' + q), u: 'https://www.youtube.com/results?search_query=' + encodeURIComponent(q), k: 'video' };
  };

  /* =====================================================================
     TEMPLATE LIBRARY
     Each template returns a full item object.
     `d` is the target difficulty; templates scale their numbers to it.
     ===================================================================== */
  var T = {};

  /* ---- Percentage change ---------------------------------------------- */
  T.percentChange = function (r, d) {
    var scale = d === 'easy' ? 10 : d === 'moderate' ? 25 : 60;
    var before = ri(r, 2, scale) * 10;
    var pct = pick(r, d === 'easy' ? [10, 20, 25, 50] : [12, 15, 18, 24, 35, 42]);
    var up = r() > 0.5;
    var after = Math.round(before * (up ? 1 + pct / 100 : 1 - pct / 100));
    var actual = Math.round(Math.abs(after - before) / before * 1000) / 10;
    var wrongBase = Math.round(Math.abs(after - before) / after * 1000) / 10;
    var m = mcq(r, actual, [wrongBase, Math.round(actual * 10) / 10 + 5, Math.abs(after - before)], function (v) { return v + '%'; });
    return {
      section: 'quantitative', difficulty: d, type: 'mcq',
      q: 'A value ' + (up ? 'rose' : 'fell') + ' from ' + before + ' to ' + after + '. What was the percentage ' + (up ? 'increase' : 'decrease') + '?',
      opts: m.opts, a: m.a,
      why: '<p>Percent change always divides by the <strong>original</strong> value:</p>' +
        '<p class="xp-math">|' + after + ' &minus; ' + before + '| = ' + Math.abs(after - before) + '<br>' +
        Math.abs(after - before) + ' / ' + before + ' = ' + (actual / 100).toFixed(4) + ' = <strong>' + actual + '%</strong></p>' +
        '<p>Dividing by the new value (' + after + ') instead gives ' + wrongBase + '%, which is the distractor built into this item. ' +
        'The original is the reference point because you are measuring change <em>relative to where you started</em>.</p>',
      refs: [YT('percent increase decrease original value', 'Video — Percent change')]
    };
  };

  /* ---- Linear equation ------------------------------------------------ */
  T.linearEq = function (r, d) {
    var a = ri(r, 2, d === 'easy' ? 5 : 9);
    var b = ri(r, -12, 12) || 3;
    var x = ri(r, -8, 12);
    var c = ri(r, 1, d === 'easy' ? 3 : 7);
    var rhs = a * x + b - c * x;
    return {
      section: 'algebra', difficulty: d, type: 'entry',
      q: 'Solve for x: ' + a + 'x + ' + (b < 0 ? '(' + b + ')' : b) + ' = ' + c + 'x + ' + rhs + '. Enter the value of x.',
      a: x, tol: 0,
      why: '<p>Collect the variable terms on one side and the constants on the other:</p>' +
        '<p class="xp-math">' + a + 'x + ' + b + ' = ' + c + 'x + ' + rhs + '<br>' +
        (a - c) + 'x = ' + (rhs - b) + '<br>x = ' + (rhs - b) + ' / ' + (a - c) + ' = <strong>' + x + '</strong></p>' +
        '<p>Check by substituting back:</p>' +
        '<p class="xp-math">Left: ' + a + '(' + x + ') + ' + b + ' = ' + (a * x + b) + '<br>Right: ' + c + '(' + x + ') + ' + rhs + ' = ' + (c * x + rhs) + ' &#10003;</p>' +
        '<p>Substitution takes seconds and catches the sign errors that cause most losses on this item type.</p>',
      refs: [YT('solving linear equations variables both sides', 'Video — Variables on both sides')]
    };
  };

  /* ---- Ratio / mixture ------------------------------------------------ */
  T.ratio = function (r, d) {
    var p1 = ri(r, 1, 5), p2 = ri(r, 2, 12);
    var total = pick(r, [200, 250, 400, 500, 600, 750, 1000]);
    var partVal = Math.round(p1 / (p1 + p2) * total * 100) / 100;
    var wrong = Math.round(p1 / p2 * total * 100) / 100;
    var m = mcq(r, partVal, [wrong, Math.round(p2 / (p1 + p2) * total * 100) / 100, Math.round(total / (p1 + p2) * 100) / 100], function (v) { return v + ' mL'; });
    return {
      section: 'quantitative', difficulty: d, type: 'mcq',
      q: 'A mixture is prepared in a ratio of ' + p1 + ' parts concentrate to ' + p2 + ' parts diluent. How much concentrate is required to make ' + total + ' mL of the mixture?',
      opts: m.opts, a: m.a,
      why: '<p>The ratio ' + p1 + ':' + p2 + ' describes <em>parts to parts</em>. The total is ' + p1 + ' + ' + p2 + ' = <strong>' + (p1 + p2) + ' parts</strong>.</p>' +
        '<p class="xp-math">Concentrate = (' + p1 + ' / ' + (p1 + p2) + ') &times; ' + total + ' mL = <strong>' + partVal + ' mL</strong></p>' +
        '<p>Using ' + p1 + '/' + p2 + ' instead gives ' + wrong + ' mL — the classic error of treating a part-to-part ratio as a fraction of the whole. ' +
        'Whenever a question asks for a share of a <em>total</em>, the denominator must be the sum of all parts.</p>',
      refs: [YT('ratio part to whole mixture problems', 'Video — Part-to-part vs part-to-whole')]
    };
  };

  /* ---- Dosage --------------------------------------------------------- */
  T.dosage = function (r, d) {
    var have = pick(r, [125, 250, 500, 50, 100]);
    var perML = pick(r, [5, 10, 2, 1]);
    var mult = pick(r, d === 'easy' ? [1, 2] : [1.5, 2.5, 0.5, 3]);
    var desired = have * mult;
    var vol = Math.round(desired / have * perML * 100) / 100;
    var m = mcq(r, vol, [Math.round(have / desired * perML * 100) / 100, vol * 2, vol / 2], function (v) { return v + ' mL'; });
    return {
      section: 'clinical', difficulty: d, type: 'mcq',
      q: 'The order reads ' + desired + ' mg. Available: ' + have + ' mg per ' + perML + ' mL. How many millilitres should be given?',
      opts: m.opts, a: m.a,
      why: '<p>Desired over have, times the quantity on hand:</p>' +
        '<p class="xp-math">(' + desired + ' / ' + have + ') &times; ' + perML + ' mL = ' + mult + ' &times; ' + perML + ' = <strong>' + vol + ' mL</strong></p>' +
        '<p>Ratio check: the ordered dose is ' + mult + '&times; the concentration on hand, so the volume must be ' + mult + '&times; ' + perML + ' mL. It is.</p>' +
        '<p>That check is not optional in practice — it is what catches a tenfold decimal error before it reaches a patient.</p>',
      refs: [YT('desired over have dosage calculation practice', 'Video — Dosage calculation basics')]
    };
  };

  /* ---- Statistics ----------------------------------------------------- */
  T.stats = function (r, d) {
    var n = d === 'easy' ? 5 : ri(r, 6, 8);
    var vals = [];
    for (var i = 0; i < n; i++) vals.push(ri(r, 4, 40));
    if (d !== 'easy') vals[n - 1] = ri(r, 70, 120); /* inject an outlier */
    var sorted = vals.slice().sort(function (a, b) { return a - b; });
    var sum = vals.reduce(function (a, b) { return a + b; }, 0);
    var mean = Math.round(sum / n * 100) / 100;
    var median = n % 2 ? sorted[(n - 1) / 2] : (sorted[n / 2 - 1] + sorted[n / 2]) / 2;
    var askMean = r() > 0.5;
    var target = askMean ? mean : median;
    var m = mcq(r, target, [askMean ? median : mean, sorted[0], sorted[n - 1]], function (v) { return String(v); });
    return {
      section: 'statistics', difficulty: d, type: 'mcq',
      q: 'For the data set ' + vals.join(', ') + ', what is the ' + (askMean ? 'mean' : 'median') + '?',
      opts: m.opts, a: m.a,
      why: '<p>Sorted: ' + sorted.join(', ') + '</p>' +
        '<p class="xp-math">Mean = ' + sum + ' / ' + n + ' = ' + mean + '<br>Median = ' + median + (n % 2 ? ' (middle value)' : ' (average of the two middle values)') + '</p>' +
        '<p>The answer is <strong>' + target + '</strong>.</p>' +
        (d !== 'easy'
          ? '<p>Note the outlier at ' + vals[n - 1] + '. It pulls the mean well above the median — which is exactly why median is the preferred measure of centre for skewed data such as income or hospital length of stay.</p>'
          : '<p>With an odd count the median is simply the middle value once sorted; sorting first is not optional.</p>'),
      refs: [YT('mean median outliers measures of central tendency', 'Video — Mean, median and outliers')]
    };
  };

  /* ---- Unit conversion ------------------------------------------------ */
  T.convert = function (r, d) {
    var sets = [
      { from: 'lb', to: 'kg', f: 2.2, dir: 'div' },
      { from: 'kg', to: 'lb', f: 2.2, dir: 'mul' },
      { from: 'in', to: 'cm', f: 2.54, dir: 'mul' },
      { from: 'L', to: 'mL', f: 1000, dir: 'mul' },
      { from: 'g', to: 'mg', f: 1000, dir: 'mul' },
      { from: 'mcg', to: 'mg', f: 1000, dir: 'div' }
    ];
    var s = pick(r, sets);
    var v = ri(r, 2, d === 'easy' ? 40 : 400);
    var correct = s.dir === 'mul' ? Math.round(v * s.f * 100) / 100 : Math.round(v / s.f * 100) / 100;
    var inverse = s.dir === 'mul' ? Math.round(v / s.f * 100) / 100 : Math.round(v * s.f * 100) / 100;
    var m = mcq(r, correct, [inverse, correct * 10, correct / 10], function (x) { return x + ' ' + s.to; });
    return {
      section: 'quantitative', difficulty: d, type: 'mcq',
      q: 'Convert ' + v + ' ' + s.from + ' to ' + s.to + '.',
      opts: m.opts, a: m.a,
      why: '<p>Arrange the conversion factor so the starting unit cancels:</p>' +
        '<p class="xp-math">' + v + ' ' + s.from + ' &times; (' + (s.dir === 'mul' ? s.f + ' ' + s.to + ' / 1 ' + s.from : '1 ' + s.to + ' / ' + s.f + ' ' + s.from) + ') = <strong>' + correct + ' ' + s.to + '</strong></p>' +
        '<p>Applying the factor the wrong way round gives ' + inverse + ' ' + s.to + '. Guard against it with a magnitude check before computing: decide whether the answer should be larger or smaller than ' + v + ', then confirm your result agrees.</p>',
      refs: [YT('dimensional analysis unit conversion cancel units', 'Video — Dimensional analysis')]
    };
  };

  /* ---- Geometry ------------------------------------------------------- */
  T.geometry = function (r, d) {
    var w = ri(r, 3, 25), h = ri(r, 3, 25);
    var wantArea = r() > 0.5;
    var area = w * h, per = 2 * (w + h);
    var target = wantArea ? area : per;
    var m = mcq(r, target, [wantArea ? per : area, w + h, w * h * 2], function (v) { return v + (wantArea ? ' sq units' : ' units'); });
    return {
      section: 'geometry', difficulty: d, type: 'mcq',
      q: 'A rectangle measures ' + w + ' by ' + h + ' units. What is its ' + (wantArea ? 'area' : 'perimeter') + '?',
      opts: m.opts, a: m.a,
      why: '<p class="xp-math">Area = length &times; width = ' + w + ' &times; ' + h + ' = ' + area + ' sq units<br>' +
        'Perimeter = 2(l + w) = 2(' + w + ' + ' + h + ') = ' + per + ' units</p>' +
        '<p>The answer is <strong>' + target + (wantArea ? ' sq units' : ' units') + '</strong>.</p>' +
        '<p>Keep them separate by what they measure: area covers a surface and carries <em>squared</em> units; perimeter traces the boundary and carries linear units. If a word problem mentions fencing, trim or edging it wants perimeter; paint, turf or tiling wants area.</p>',
      refs: [YT('area perimeter rectangle word problems', 'Video — Area and perimeter')]
    };
  };

  /* ---- Reading inference (applied, course-agnostic) -------------------- */
  T.applied = function (r, d, course) {
    var verbs = ['evaluate', 'apply', 'compare', 'analyse', 'justify'];
    var v = pick(r, verbs);
    var topics = (course && course.topics && course.topics.length) ? course.topics : ['the core framework', 'the principal model', 'the standard procedure'];
    var topic = pick(r, topics);
    return {
      section: 'applied', difficulty: d, type: 'mcq',
      q: 'In ' + (course ? course.name : 'this course') + ', which approach best demonstrates the ability to ' + v + ' ' + topic + ' in an unfamiliar situation?',
      opts: [
        'Identify the underlying principle, then test whether the new situation satisfies its conditions',
        'Recall the closest worked example and reproduce its steps unchanged',
        'Select the method most recently covered in the syllabus',
        'Choose whichever approach requires the fewest calculations'
      ],
      a: 0,
      why: '<p>Transfer to an unfamiliar case requires working from the <strong>principle</strong>, not from surface resemblance to a solved problem.</p>' +
        '<p>Step one is to identify what the principle actually requires — its conditions of application. Step two is to check whether the new situation meets them. If it does not, the principle does not apply, however similar the problem may look.</p>' +
        '<p>Reproducing a worked example (B) fails precisely when the new case differs in a way that matters, and you will not notice, because you never checked the conditions. Recency (C) and convenience (D) are not reasons at all.</p>' +
        '<p>This is the distinction between knowing a procedure and understanding it — and it is what midterm and final examinations are built to separate.</p>',
      refs: [YT('transfer of learning applying principles new problems', 'Video — Applying principles to novel problems')]
    };
  };

  var TEMPLATES = [T.percentChange, T.linearEq, T.ratio, T.dosage, T.stats, T.convert, T.geometry, T.applied];

  /* Templates suited to a quantitative-heavy vs. general course */
  var QUANT = [T.percentChange, T.linearEq, T.ratio, T.stats, T.convert, T.geometry];
  var CLINICAL = [T.dosage, T.convert, T.stats, T.percentChange];

  /* =====================================================================
     PUBLIC API
     ===================================================================== */

  /**
   * Generate `count` items.
   * @param {Object} o
   *   o.count      how many
   *   o.seed       integer; same seed = same paper
   *   o.difficulty easy|moderate|hard|advanced|combined
   *   o.course     { id, name, topics[], kind } — optional
   *   o.prefix     id prefix
   */
  function generate(o) {
    o = o || {};
    var count = Math.max(1, o.count || 10);
    var r = rng(o.seed || 20260726);
    var diffs = ['easy', 'moderate', 'hard', 'advanced'];
    var pool = TEMPLATES;
    if (o.course && o.course.kind === 'quant') pool = QUANT;
    if (o.course && o.course.kind === 'clinical') pool = CLINICAL;

    var out = [];
    for (var i = 0; i < count; i++) {
      var d = o.difficulty && o.difficulty !== 'combined'
        ? o.difficulty
        : diffs[i % 4];
      var tmpl = pool[i % pool.length];
      var item;
      try {
        item = tmpl(r, d, o.course);
      } catch (e) {
        item = T.applied(r, d, o.course);
      }
      item.id = (o.prefix || 'gen') + '-' + (o.seed || 0) + '-' + i;
      item.exam = o.course ? o.course.id : 'generated';
      item.generated = true;
      out.push(item);
    }
    return out;
  }

  window.XP_GEN = { generate: generate, rng: rng };
})();
